<template>
    <div class="ai-analysis-picker flex flex-col min-h-0 overflow-hidden">
        <div class="shrink-0">
            <div class="text-base font-semibold mb-1">
                {{ t('widget.aiBuilder.analysis.title') }}
            </div>
            <p class="text-main-xs text-gray-500 mb-4">
                {{ t('widget.aiBuilder.analysis.description') }}
            </p>

            <div class="flex flex-wrap items-center gap-2 mb-4">
                <button
                    v-for="chip in categoryChips"
                    :key="chip.CategoryID"
                    type="button"
                    class="flex items-center gap-1.5 px-2 py-1 rounded-md border text-sm transition-colors"
                    :class="chip.CategoryID === activeCategoryId
                        ? 'is-selected border-active-elements text-primary-actions font-semibold'
                        : 'border-ui-lines text-gray-500'"
                    @click="activeCategoryId = chip.CategoryID"
                >
                    {{ chip.CategoryName }}
                    <span class="text-main-xs text-gray-500">
                        {{ chip.count }}
                    </span>
                </button>
            </div>
        </div>

        <div class="relative flex-1 min-h-0">
            <div
                ref="gridRef"
                class="grid grid-cols-1 lg:grid-cols-2 gap-3 content-start h-full overflow-y-auto pr-1"
                @scroll="updateScrollHint"
            >
                <AiAnalysisPresetCard
                    v-for="preset in filteredPresets"
                    :key="preset.PromptTemplateID"
                    :preset="preset"
                    :category-names="categoryNamesFor(preset)"
                    :selected="preset.PromptTemplateID === value"
                    @select="$emit('select-preset', preset)"
                />

                <p
                    v-if="!filteredPresets.length"
                    class="text-main-xs text-gray-500"
                >
                    {{ isLoading
                        ? t('general.loading')
                        : t('widget.aiBuilder.analysis.empty')
                    }}
                </p>
            </div>

            <div
                v-show="hasMoreBelow"
                class="ai-analysis-picker__fade"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AiAnalysisPresetCard
    from '@/components/RedesignComponents/Widgets/Parameters/AiAnalysisPresetCard.vue'
import {
    type CategorisedPromptTemplate,
    type PromptTemplateCategory
} from '@/api/widgetParameterApi'

const ANALYSIS_CATEGORY_ALL_ID = 0

const { t } = useI18n()

const props = withDefaults(defineProps<{
    value?: number
    presets: Array<CategorisedPromptTemplate>
    categories: Array<PromptTemplateCategory>
    isLoading?: boolean
}>(), {
    value: undefined,
    isLoading: false
})

defineEmits<{
    (e: 'select-preset', preset: CategorisedPromptTemplate): void
}>()

const activeCategoryId = ref<number>(ANALYSIS_CATEGORY_ALL_ID)
const gridRef = ref<HTMLElement | null>(null)
const hasMoreBelow = ref(false)
let resizeObserver: ResizeObserver | undefined

const SCROLL_END_THRESHOLD = 4

function updateScrollHint () {
    const grid = gridRef.value
    if (!grid) {
        return
    }

    const remaining = grid.scrollHeight - grid.scrollTop - grid.clientHeight
    hasMoreBelow.value = remaining > SCROLL_END_THRESHOLD
}

const categoryNameById = computed<Record<number, string>>(() => {
    return props.categories.reduce<Record<number, string>>((acc, category) => {
        acc[category.PromptTemplateCategoryID] = category.PromptTemplateCategoryName
        return acc
    }, {})
})

function presetsInCategory (categoryId: number) {
    return props.presets.filter(preset => {
        return (preset.PromptTemplateCategoryIDs ?? []).includes(categoryId)
    })
}

function categoryNamesFor (preset: CategorisedPromptTemplate): Array<string> {
    return (preset.PromptTemplateCategoryIDs ?? [])
        .map(categoryId => categoryNameById.value[categoryId])
        .filter(Boolean)
}

const categoryChips = computed(() => {
    const chips = props.categories
        .map(category => ({
            CategoryID: category.PromptTemplateCategoryID,
            CategoryName: category.PromptTemplateCategoryName,
            count: presetsInCategory(category.PromptTemplateCategoryID).length
        }))
        .filter(chip => chip.count > 0)

    return [
        {
            CategoryID: ANALYSIS_CATEGORY_ALL_ID,
            CategoryName: String(t('common.all')),
            count: props.presets.length
        },
        ...chips
    ]
})

const filteredPresets = computed(() => {
    if (activeCategoryId.value === ANALYSIS_CATEGORY_ALL_ID) {
        return props.presets
    }
    return presetsInCategory(activeCategoryId.value)
})

watch(filteredPresets, () => nextTick(updateScrollHint))

onMounted(() => {
    nextTick(updateScrollHint)

    if (gridRef.value && typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(updateScrollHint)
        resizeObserver.observe(gridRef.value)
    }
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect()
})
</script>

<style scoped lang="scss">
.ai-analysis-picker {
    &__fade {
        @apply pointer-events-none absolute left-0 right-0 bottom-0 h-8;
        background: linear-gradient(to bottom, transparent, var(--mainBgColor-color));
    }
}
</style>
